import { z } from 'zod'

// A member's licence, ratings and medical -- member.user_licences, see
// db/flyway/schema/sql/V110__user_licences.sql for the two kinds of row.
//
// DOCUMENT types carry one uploaded file each; RATING types are written on
// the licence document and only ever carry an expiry and `held`.
export const DOCUMENT_LICENCE_TYPES = ['licence', 'medical', 'radio'] as const
export const RATING_LICENCE_TYPES = ['sep', 'ir', 'iri', 'fi', 'fe', 'fie', 'cof'] as const
export const LICENCE_TYPES = [...DOCUMENT_LICENCE_TYPES, ...RATING_LICENCE_TYPES] as const
export type LicenceType = (typeof LICENCE_TYPES)[number]
export const LicenceTypeSchema = z.enum(LICENCE_TYPES)

export function isDocumentLicenceType(type: LicenceType): boolean {
  return (DOCUMENT_LICENCE_TYPES as readonly string[]).includes(type)
}

// Class 1 > Class 2 > LAPL: a higher class satisfies a lower requirement.
export const MEDICAL_CLASSES = ['class1', 'class2', 'lapl'] as const
export type MedicalClass = (typeof MEDICAL_CLASSES)[number]
export const MedicalClassSchema = z.enum(MEDICAL_CLASSES)

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD')

// PUT /api/members/:id/licences/:type. Every field is optional and an
// omitted one keeps its stored value, so ticking "I don't have this
// rating" can send just `held` and editing the date later just
// `expiryDate`.
export const UpsertLicenceSchema = z
  .object({
    expiryDate: IsoDateSchema.nullable().optional(),
    held: z.boolean().nullable().optional(),
    medicalClass: MedicalClassSchema.nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'No fields to update' })
export type UpsertLicenceInput = z.infer<typeof UpsertLicenceSchema>

export interface MemberLicence {
  licenceType: LicenceType
  held: boolean | null
  expiryDate: string | null
  medicalClass: MedicalClass | null
  documentFilename: string | null
  documentContentType: string | null
  updatedAt: string
}

// How close to expiry counts as "renew soon" on the profile and in the
// admin expiring-documents report.
export const EXPIRY_WARNING_DAYS = 60

export type ExpiryStatus = 'none' | 'valid' | 'expiring' | 'expired'

// `today` is a YYYY-MM-DD in the club's timezone, supplied by the caller
// (the server clock on the frontend, the club timezone on the API) --
// never `new Date()` here, which would be the browser's or UTC's day.
// Compared as strings: both sides are date-only ISO values.
export function expiryStatus(
  expiryDate: string | null | undefined,
  today: string,
  warningDays = EXPIRY_WARNING_DAYS,
): ExpiryStatus {
  if (!expiryDate) return 'none'
  if (expiryDate < today) return 'expired'
  const warnFrom = new Date(`${today}T00:00:00Z`)
  warnFrom.setUTCDate(warnFrom.getUTCDate() + warningDays)
  return expiryDate <= warnFrom.toISOString().slice(0, 10) ? 'expiring' : 'valid'
}
